/**
 * TournamentContext - Americano tournament state management
 *
 * State shape mirrors the Firebase document structure (americano collection).
 * detailedScore is stored as plain objects (not class instances) for easy serialization.
 */
import React, { createContext, useContext, useReducer, useCallback } from 'react';
import DEFAULT_CONFIG from '../config/defaultConfig.json';

function buildDefaultConfig() {
  const count = DEFAULT_CONFIG.courtCount || 1;
  const courtNames = Array.from({ length: count }, (_, i) => `Terrain ${i + 1}`);
  return { ...DEFAULT_CONFIG, courtNames };
}

// ─── Ranking helpers ──────────────────────────────────────────────────────────

function initRanking(players) {
  return players.map(player => ({
    playerId: player.id,
    name: player.name,
    totalPoints: 0,
    matchesPlayed: 0,
    averagePoints: 0,
    partnersPlayed: []
  }));
}

function buildInitialState() {
  const config = buildDefaultConfig();
  const players = Array.from({ length: config.playerCount }, (_, i) => ({
    id: i,
    name: `Joueur ${i + 1}`
  }));
  return {
    mode: 'americano',
    config,
    players,
    ranking: initRanking(players),
    schedule: { rounds: [] },
    scheduleSeed: null,
    currentRound: 0,
    scheduleGenerated: false,
    tournamentStarted: false,
    tournamentFinished: false,
    startTime: null
  };
}

const INITIAL_STATE = buildInitialState();

function recalculateRanking(state) {
  const ranking = initRanking(state.players);

  state.schedule.rounds.forEach(round => {
    round.matches.forEach(match => {
      const score = match.detailedScore;
      if (!match.validated || !score || !score.isComplete) return;

      const processTeam = (teamIds, points) => {
        teamIds.forEach(playerId => {
          const entry = ranking.find(r => r.playerId === playerId);
          if (!entry) return;

          entry.totalPoints += points;
          entry.matchesPlayed++;

          const partnerId = teamIds.find(id => id !== playerId);
          if (partnerId !== undefined && !entry.partnersPlayed.includes(partnerId)) {
            entry.partnersPlayed.push(partnerId);
          }

          entry.averagePoints = entry.matchesPlayed > 0
            ? entry.totalPoints / entry.matchesPlayed
            : 0;
        });
      };

      processTeam(match.team1, score.team1Points);
      processTeam(match.team2, score.team2Points);
    });
  });

  // Sort: totalPoints DESC → averagePoints DESC → name ASC
  ranking.sort((a, b) => {
    if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
    if (b.averagePoints !== a.averagePoints) return b.averagePoints - a.averagePoints;
    return a.name.localeCompare(b.name);
  });

  return ranking;
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

function tournamentReducer(state, action) {
  switch (action.type) {

    case 'SET_CONFIG': {
      return {
        ...state,
        config: { ...state.config, ...action.payload },
        scheduleGenerated: false
      };
    }

    case 'SET_PLAYERS': {
      const players = action.payload.map((data, index) => ({
        id: index,
        name: data.name.trim()
      }));
      return {
        ...state,
        players,
        ranking: initRanking(players),
        scheduleGenerated: false,
        schedule: { rounds: [] }
      };
    }

    case 'UPDATE_PLAYER_NAMES': {
      const newNames = action.payload;
      const players = state.players.map((p, i) => ({
        ...p,
        name: newNames[i] !== undefined ? newNames[i].trim() : p.name
      }));
      const ranking = state.ranking.map(r => {
        const player = players.find(p => p.id === r.playerId);
        return player ? { ...r, name: player.name } : r;
      });
      return { ...state, players, ranking };
    }

    case 'SET_SCHEDULE': {
      const { schedule, seed } = action.payload;

      // Initialize detailedScore as plain objects
      const rounds = schedule.rounds.map(round => ({
        ...round,
        matches: round.matches.map(match => ({
          ...match,
          validated: false,
          detailedScore: {
            totalPoints: state.config.pointsPerMatch,
            team1Points: 0,
            team2Points: 0,
            isComplete: false
          }
        }))
      }));

      // Exclude calculation matrices not needed for persistence (would cause Firestore nested array errors)
      const { partnerMatrix, opponentMatrix, sitOutCount, rotationStats, warnings, ...scheduleToStore } = schedule;

      return {
        ...state,
        schedule: { ...scheduleToStore, rounds },
        scheduleSeed: seed || null,
        scheduleGenerated: true,
        currentRound: 0,
        tournamentStarted: false,
        tournamentFinished: false,
        ranking: initRanking(state.players)
      };
    }

    case 'START_TOURNAMENT': {
      return {
        ...state,
        tournamentStarted: true,
        startTime: new Date().toISOString()
      };
    }

    case 'VALIDATE_MATCH': {
      const { roundIndex, matchIndex, team1Points, team2Points } = action.payload;
      const rounds = state.schedule.rounds.map((round, ri) => {
        if (ri !== roundIndex) return round;
        return {
          ...round,
          matches: round.matches.map((match, mi) => {
            if (mi !== matchIndex) return match;
            return {
              ...match,
              validated: true,
              detailedScore: {
                totalPoints: state.config.pointsPerMatch,
                team1Points,
                team2Points,
                isComplete: (team1Points + team2Points) === state.config.pointsPerMatch
              }
            };
          })
        };
      });

      const newState = {
        ...state,
        schedule: { ...state.schedule, rounds }
      };

      return {
        ...newState,
        ranking: recalculateRanking(newState)
      };
    }

    case 'UNVALIDATE_MATCH': {
      const { roundIndex, matchIndex } = action.payload;
      const rounds = state.schedule.rounds.map((round, ri) => {
        if (ri !== roundIndex) return round;
        return {
          ...round,
          matches: round.matches.map((match, mi) => {
            if (mi !== matchIndex) return match;
            return {
              ...match,
              validated: false,
              detailedScore: {
                totalPoints: state.config.pointsPerMatch,
                team1Points: 0,
                team2Points: 0,
                isComplete: false
              }
            };
          })
        };
      });

      const newState = { ...state, schedule: { ...state.schedule, rounds } };
      return { ...newState, ranking: recalculateRanking(newState) };
    }

    case 'SET_CURRENT_ROUND': {
      const idx = action.payload;
      if (idx < 0 || idx >= state.schedule.rounds.length) return state;
      return { ...state, currentRound: idx };
    }

    case 'ADVANCE_ROUND': {
      const nextRound = state.currentRound + 1;
      const isLast = nextRound >= state.schedule.rounds.length;
      return {
        ...state,
        currentRound: isLast ? state.currentRound : nextRound,
        tournamentFinished: isLast
      };
    }

    case 'FINISH_TOURNAMENT': {
      return { ...state, tournamentFinished: true };
    }

    case 'RESET': {
      return buildInitialState();
    }

    case 'IMPORT_STATE': {
      const data = action.payload;

      // Deserialize schedule: ensure detailedScore is plain object
      let schedule = { rounds: [] };
      if (data.schedule && data.schedule.rounds) {
        schedule = {
          ...data.schedule,
          rounds: data.schedule.rounds.map(round => ({
            ...round,
            matches: (round.matches || []).map(match => ({
              ...match,
              validated: match.validated || false,
              detailedScore: match.detailedScore
                ? { ...match.detailedScore }
                : {
                    totalPoints: data.config?.pointsPerMatch || 24,
                    team1Points: 0,
                    team2Points: 0,
                    isComplete: false
                  }
            }))
          }))
        };
      }

      const ranking = (data.ranking || []).map(r => ({
        playerId: r.playerId,
        name: r.name,
        totalPoints: r.totalPoints || 0,
        matchesPlayed: r.matchesPlayed || 0,
        averagePoints: r.averagePoints || 0,
        partnersPlayed: r.partnersPlayed || []
      }));

      return {
        mode: 'americano',
        config: { ...DEFAULT_CONFIG, ...(data.config || {}) },
        players: data.players || [],
        ranking,
        schedule,
        scheduleSeed: data.scheduleSeed || null,
        currentRound: data.currentRound || 0,
        scheduleGenerated: data.scheduleGenerated || false,
        tournamentStarted: data.tournamentStarted || false,
        tournamentFinished: data.tournamentFinished || false,
        startTime: data.startTime || null
      };
    }

    default:
      return state;
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

const TournamentContext = createContext(null);

export function TournamentProvider({ children }) {
  const [state, dispatch] = useReducer(tournamentReducer, INITIAL_STATE);

  // ── Actions ──

  const setConfig = useCallback((config) => {
    dispatch({ type: 'SET_CONFIG', payload: config });
  }, []);

  const setPlayers = useCallback((players) => {
    dispatch({ type: 'SET_PLAYERS', payload: players });
  }, []);

  const updatePlayerNames = useCallback((names) => {
    dispatch({ type: 'UPDATE_PLAYER_NAMES', payload: names });
  }, []);

  const setSchedule = useCallback((schedule, seed) => {
    dispatch({ type: 'SET_SCHEDULE', payload: { schedule, seed } });
  }, []);

  const startTournament = useCallback(() => {
    dispatch({ type: 'START_TOURNAMENT' });
  }, []);

  const validateMatch = useCallback((roundIndex, matchIndex, team1Points, team2Points) => {
    dispatch({ type: 'VALIDATE_MATCH', payload: { roundIndex, matchIndex, team1Points, team2Points } });
  }, []);

  const unvalidateMatch = useCallback((roundIndex, matchIndex) => {
    dispatch({ type: 'UNVALIDATE_MATCH', payload: { roundIndex, matchIndex } });
  }, []);

  const setCurrentRound = useCallback((index) => {
    dispatch({ type: 'SET_CURRENT_ROUND', payload: index });
  }, []);

  const advanceRound = useCallback(() => {
    dispatch({ type: 'ADVANCE_ROUND' });
  }, []);

  const finishTournament = useCallback(() => {
    dispatch({ type: 'FINISH_TOURNAMENT' });
  }, []);

  const resetTournament = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // ── Firebase helpers ──

  const exportState = useCallback(() => {
    return {
      mode: state.mode,
      config: state.config,
      players: state.players,
      ranking: state.ranking,
      schedule: state.schedule,
      scheduleSeed: state.scheduleSeed,
      currentRound: state.currentRound,
      scheduleGenerated: state.scheduleGenerated,
      tournamentStarted: state.tournamentStarted,
      tournamentFinished: state.tournamentFinished,
      startTime: state.startTime
    };
  }, [state]);

  const importState = useCallback((data) => {
    dispatch({ type: 'IMPORT_STATE', payload: data });
  }, []);

  // ── Computed helpers ──

  const getPlayerName = useCallback((id) => {
    const player = state.players.find(p => p.id === id);
    return player ? player.name : `Joueur ${id + 1}`;
  }, [state.players]);

  const getCurrentRound = useCallback(() => {
    if (!state.schedule.rounds || state.schedule.rounds.length === 0) return null;
    return state.schedule.rounds[state.currentRound] || null;
  }, [state.schedule.rounds, state.currentRound]);

  const isCurrentRoundComplete = useCallback(() => {
    const round = getCurrentRound();
    if (!round) return false;
    return round.matches.every(m => m.validated && m.detailedScore?.isComplete);
  }, [getCurrentRound]);

  const value = {
    state,
    // Actions
    setConfig,
    setPlayers,
    updatePlayerNames,
    setSchedule,
    startTournament,
    validateMatch,
    unvalidateMatch,
    setCurrentRound,
    advanceRound,
    finishTournament,
    resetTournament,
    // Firebase
    exportState,
    importState,
    // Helpers
    getPlayerName,
    getCurrentRound,
    isCurrentRoundComplete
  };

  return (
    <TournamentContext.Provider value={value}>
      {children}
    </TournamentContext.Provider>
  );
}

export function useTournament() {
  const context = useContext(TournamentContext);
  if (!context) {
    throw new Error('useTournament must be used within a TournamentProvider');
  }
  return context;
}

export default TournamentContext;
